<script setup lang="ts">
// Home hero — mockup page 1.
//
// The strategy note is blunt about this screen: a homeowner arriving from
// Google should not have to work out what the site does. So it says the
// problem out loud, then offers exactly three doors, then one CTA. Nothing
// else competes.
import { RouterLink } from 'vue-router'
import { leadIntent } from '@/shared/state/leadIntent'
// Pixabay Content License. Borrowed from the ror brand as a placeholder
// until the redesign's own hero photography is sourced.
import heroPhoto from '@/assets/ror-condensers.jpg'

/** "I Need a New AC System" is a visitor stating replacement intent, even
 * though it routes to the comparison page first. Recording it here means
 * the contact form is already in replace mode by the time they reach it,
 * and the relayed lead is typed from the visitor rather than inferred. */
function onChoose(intent?: 'replace') {
    if (intent) leadIntent.value = intent
}

const CHOICES = [
    {
        to: '/ask',
        title: "My AC Isn't Working",
        sub: 'Troubleshoot the problem',
        icon: '<path d="M14.7 6.3a4 4 0 0 1-5.4 5.4l-5 5a1.9 1.9 0 0 1-2.7-2.7l5-5a4 4 0 0 1 5.4-5.4L9.6 5.9l1.2 3.3 3.3 1.2z"/>',
    },
    {
        to: '/repair-or-replace',
        title: 'Should I Repair or Replace It?',
        sub: 'Compare your options',
        icon: '<path d="M10 2.2v2.1M3.4 5.6h13.2M6.3 5.6 3.4 12h5.8zM13.7 5.6 10.8 12h5.8zM7.4 17.2h5.2v-1.4H7.4z"/><path d="M9.2 4.9h1.6v11.6H9.2z"/>',
    },
    {
        to: '/system-types',
        intent: 'replace' as const,
        title: 'I Need a New AC System',
        sub: 'Explore new systems',
        icon: '<path d="M3.2 4.4h13.6a1 1 0 0 1 1 1v9.2a1 1 0 0 1-1 1H3.2a1 1 0 0 1-1-1V5.4a1 1 0 0 1 1-1zm1.4 2.4v6.4h10.8V6.8zM6 8.2h8v1.3H6zm0 2.6h5.4V12H6z"/>',
    },
]

const BADGES = [
    { label: 'Florida-Focused AC Advice', icon: '<path d="M10 2.5c-1.7 3.1-5.2 4.8-5.2 8.4a5.2 5.2 0 0 0 10.4 0c0-3.6-3.5-5.3-5.2-8.4z"/>' },
    { label: 'Local HVAC Guidance', icon: '<path d="M10 1.8a5.2 5.2 0 0 0-5.2 5.2c0 3.9 5.2 11.2 5.2 11.2s5.2-7.3 5.2-11.2A5.2 5.2 0 0 0 10 1.8zm0 7.2a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>' },
    { label: 'Homeowner Friendly', icon: '<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>' },
    { label: 'Unbiased Information', icon: '<path d="M10 1.6 3 4.6v5.2c0 4.3 3 8.3 7 9.2 4-0.9 7-4.9 7-9.2V4.6z"/>' },
]
</script>

<template>
    <section class="hero" :style="{ backgroundImage: `url(${heroPhoto})` }">
        <div class="wash" aria-hidden="true"></div>

        <div class="inner">
            <div class="copy">
                <h1>
                    Having an AC Problem?<br />
                    Get an <span class="hl">Answer</span> Before<br />
                    You Call an HVAC Company.
                </h1>
                <p class="lede">
                    Tell us what's happening with your system. We'll help you understand the likely
                    problem, what it may cost, and whether repairing or replacing makes more sense.
                </p>

                <div class="choices">
                    <RouterLink
                        v-for="c in CHOICES"
                        :key="c.to"
                        class="choice"
                        :to="c.to"
                        @click="onChoose(c.intent)"
                    >
                        <svg class="choice-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="c.icon"></svg>
                        <span class="choice-title">{{ c.title }}</span>
                        <span class="choice-sub">{{ c.sub }}</span>
                        <span class="arrow" aria-hidden="true">›</span>
                    </RouterLink>
                </div>

                <RouterLink class="expert-cta" to="/ask">
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M3 3.5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H8l-4 3v-3H3a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z" />
                    </svg>
                    <span class="stack">
                        <strong>Ask the HVAC Expert →</strong>
                        <small>Get a Personalized Assessment in Minutes</small>
                    </span>
                </RouterLink>
            </div>

            <ul class="badges">
                <li v-for="b in BADGES" :key="b.label">
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="b.icon"></svg>
                    <span>{{ b.label }}</span>
                </li>
            </ul>
        </div>
    </section>
</template>

<style scoped lang="scss">
.hero {
    position: relative;
    background-size: cover;
    background-position: center right;
    color: #fff;
    overflow: hidden;
}

.wash {
    position: absolute;
    inset: 0;
    background: var(--site-gradient-hero-overlay);
}

.inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: clamp(2.5rem, 6vw, 4.5rem) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: var(--site-space-xl);
    align-items: center;
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.8rem, 3.6vw, 2.85rem);
    font-weight: 800;
    line-height: 1.16;
    margin: 0 0 var(--site-space-md);
    text-shadow: 0 2px 18px rgba(3, 15, 40, 0.45);
}

.lede {
    max-width: 40rem;
    margin: 0 0 var(--site-space-lg);
    font-size: 0.98rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
}

.choices {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--site-space-md);
    margin-bottom: var(--site-space-lg);

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
}

.choice {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: var(--site-space-md) var(--site-space-md) var(--site-space-lg);
    background: #fff;
    border-radius: var(--site-radius-md);
    box-shadow: var(--site-shadow-md);
    text-decoration: none;
    transition: transform 0.14s ease, box-shadow 0.14s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: var(--site-shadow-lg);
    }
}

.choice-icon {
    width: 1.7rem;
    height: 1.7rem;
    color: var(--site-blue);
    margin-bottom: 0.35rem;
}

.choice-title {
    font-family: var(--site-font-heading);
    font-size: 0.98rem;
    font-weight: 700;
    color: var(--site-navy);
    line-height: 1.25;
}

.choice-sub {
    font-size: 0.78rem;
    color: var(--site-text-muted);
}

.arrow {
    position: absolute;
    right: 0.75rem;
    bottom: 0.6rem;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 50%;
    background: var(--site-accent);
    color: #fff;
    font-size: 1rem;
    line-height: 1.25rem;
    text-align: center;
}

.expert-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.7rem 1.4rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    text-decoration: none;
    box-shadow: var(--site-glow);

    svg {
        width: 1.4rem;
        height: 1.4rem;
    }

    .stack {
        display: flex;
        flex-direction: column;
        line-height: 1.2;
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 1.02rem;
        font-weight: 700;
    }

    small {
        font-size: 0.72rem;
        color: rgba(255, 255, 255, 0.88);
    }

    &:hover {
        background: var(--site-accent-strong);
    }
}

.badges {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--site-space-sm);
    width: 13.5rem;

    li {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.6rem 0.85rem;
        border-radius: var(--site-radius-sm);
        background: rgba(7, 28, 69, 0.72);
        border: 1px solid rgba(255, 255, 255, 0.14);
        font-size: 0.78rem;
        font-weight: 600;
    }

    svg {
        width: 1.5rem;
        height: 1.5rem;
        flex-shrink: 0;
        color: #6ea8f5;
    }

    @media (max-width: 1000px) {
        display: none;
    }
}

@media (max-width: 1000px) {
    .inner {
        grid-template-columns: 1fr;
    }
}
</style>
